import { Model } from 'app/model/backendmodel';
import { AdapterContext, AdapterRegistry, IModel } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { StoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/store-read-settings';
import { StoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/store-write-settings';
import { AzureBlobFSStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/azure-blob-fs-read-settings';
import { AzureBlobReadSettings } from 'app/model/pipeline/copysource/store-read-settings/azure-blob-read-settings';
import { AzureDataLakeStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/azure-data-lake-store-read-settings';
import { S3StoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/s3-store-read-settings';
import { FtpStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/ftp-store-read-settings';
import { FileServerStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/file-server-store-read-settings';
import { SftpStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/sftp-store-read-settings';
import { HdfsStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/hdfs-store-read-settings';
import { convertTypeToPlural } from 'app/model/pipeline/type.util';
import { FormatTypeCopySource } from 'app/model/pipeline/copysource/format-type.copysource';
import { GenericStoreReadSettingsAdapter } from '../generic.store-read-settings.adapter';
import { GenericStoreWriteSettingsAdapter } from '../generic.store-write-settings.adapter';
import { FormatTypeCopySink } from 'app/model/pipeline/copysink/format-type.copysink';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { SharePointOnlineFileStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/sharepoint-online-file-store-read-settings';
import { LakehouseReadSettings } from 'app/model/pipeline/copysource/store-read-settings/lakehouse-read-settings';
import { merge } from 'lodash';

export async function convertStoreSettingsToResource(storeSettings: StoreReadSettings | StoreWriteSettings, result: { storeSettings?: Model.StoreReadSettings | Model.StoreWriteSettings; }
    , context: AdapterContext): Promise<void> {
    if (storeSettings == null) {
        return undefined;
    }
    const readSettingAdapter = context.asyncAdapterFor(Metamodel.modelOf(storeSettings));
    if (readSettingAdapter != null) {
        result.storeSettings = await readSettingAdapter.toResource(storeSettings);

        // handle raw data
        if (storeSettings.raw != null) {
            const parsed = JSON.parse(storeSettings.raw);
            merge(result.storeSettings, parsed);
        }
    }
}

export async function convertStoreSettingsToModel(resource: Model.StoreReadSettings | Model.StoreWriteSettings, entity: { storeSettings?: StoreReadSettings | StoreWriteSettings },
    context: AdapterContext): Promise<void> {
    if (resource == null) {
        return;
    }
    resource.type = convertTypeToPlural(resource.type);
    if (resource.type) {
        let storeSettingsModel: IModel;
        try {
            storeSettingsModel = Metamodel.modelByName(resource.type);
        } catch {
            // for connector extensibility usage, when we hit unrecognized location resource type, we'd be able to load it back on UI to enable user edit it via JSON editor.
            storeSettingsModel = entity instanceof FormatTypeCopySource ? _registerStoreReadSettingsModel(resource.type) : entity instanceof FormatTypeCopySink ?
                _registerStoreWriteSettingsModel(resource.type) : undefined;
        }
        const readSettingAdapter = storeSettingsModel != null ? context.asyncAdapterFor(storeSettingsModel) : undefined;
        if (readSettingAdapter != null) {
            entity.storeSettings = await readSettingAdapter.toModel(resource, entity);

            // handle raw data
            if (entity.storeSettings != null) {
                entity.storeSettings.raw = JSON.stringify(await ValueAdapter.remainder(context, resource, storeSettingsModel));
            }
        }
    }
}

function _registerStoreReadSettingsModel(storeReadSettingsType: string): IModel {
    const model = Metamodel.createModel(StoreReadSettings.MODEL, {
        name: storeReadSettingsType,
        display: [storeReadSettingsType],
        createdFromUnknownType: true
    });
    AdapterRegistry.createAdapter({
        model,
        asynchronous: true,
        adapts: {
            type: '*'
        }
    }, GenericStoreReadSettingsAdapter);
    return model;
}

function _registerStoreWriteSettingsModel(storeWriteSettingsType: string): IModel {
    const model = Metamodel.createModel(StoreWriteSettings.MODEL, {
        name: storeWriteSettingsType,
        display: [storeWriteSettingsType],
        createdFromUnknownType: true
    });
    AdapterRegistry.createAdapter({
        model,
        asynchronous: true,
        adapts: {
            type: '*'
        }
    }, GenericStoreWriteSettingsAdapter);
    return model;
}

export function storeSettingsWithWildcardFileNameDefinition(storeSettingType: string): boolean {
    return (storeSettingType === Metamodel.modelFor(AzureBlobFSStoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(LakehouseReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(AzureBlobReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(AzureDataLakeStoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(S3StoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(FileServerStoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(FtpStoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(SftpStoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(HdfsStoreReadSettings).name) ||
        (storeSettingType === Metamodel.modelFor(SharePointOnlineFileStoreReadSettings).name);
}
